import React, { useMemo, useState, useEffect } from 'react'
import { BatteryProviderContext } from 'src/Libs/batteries/providers/context'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import {
  fetchBatteryTrainingMode,
  fetchRscpBatteries,
  fetchRscpBatteriesMaster,
  fetchRscpBattery,
  updateBatteryTrainingMode,
  updateRscpBatterySettings,
  getBatteryPowerManagementByStorage,
} from 'src/Libs/batteries/api/batteries.api'
import { FarmBatteryModel, settingsFormToDto, BatteryPowerManagementModel } from 'src/Libs/batteries'
import { SettingsFormValues } from 'src/Elements/SystemSettings/Battery/types'
import { isUndefined } from 'lodash'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { BatteryTrainingModel } from 'src/Libs/system-data-handler/models/systemDataStoreModel'

import usePrevious from '@e3dc-react/shell/Hooks/usePrevious'
import { useFarmContext } from 'src/Elements/Farm'
import usePortalQuery from 'src/Libs/api/hooks/use-portal-query'

interface UserBatteriesHook {
  /**
   * Update the settings for the given battery- and storage-reference
   *
   * @param storageSerialNr
   * @param batteryId
   * @param values
   */
  updateSettings: (storageSerialNr: string, batteryId: string, values: SettingsFormValues) => Promise<unknown>

  /**
   * Get the battery training for a battery belonging to a specific storage
   * @param storageSerialNr
   * @param batteryId
   */
  getBatteryTraining: (storageSerialNr: string, batteryId: string) => Promise<BatteryTrainingModel | null>

  /**
   * Update the battery training mode of a battery belonging to a specific storage.
   * @param storageSerialNr
   * @param batteryId
   * @param trainingMode
   */
  updateBatteryTraining: (storageSerialNr: string, batteryId: string, trainingMode: boolean) => Promise<void>

  /**
   * A list of currently available and previously loaded battery settings.
   *
   * The containing information can be part of the storage-reference given by calling the hook, or the reference used when calling findByStorage.
   *
   * Depending on how the actual hook is used.
   */
  batteries: FarmBatteryModel[]
  error?: AuthFetchError | Error
  isLoading: boolean
}

/**
 * The hook provides tools to load and update the battery-settings for a single, initial provided storage-reference.
 *
 * When calling or, after updating battery settings, the up-to-date settings will be cached and provided through the
 * exposed `batteries`-property.
 *
 * @param storageSerialNr
 */
export const useBatteries = (storageSerialNo?: string): UserBatteriesHook => {
  const contextValue = React.useContext(BatteryProviderContext)
  if (contextValue === undefined) {
    throw new Error('useBatteries must be used within BatteryProvider')
  }
  const { batteries, setBatteries } = contextValue
  const authFetch = useAuthFetchWithResponse()
  const [error, setError] = useState<Error | AuthFetchError>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { isFarmMasterSelected } = useFarmContext()

  const valuesByStorage = useMemo<FarmBatteryModel[]>(() => {
    if (isUndefined(storageSerialNo)) {
      return []
    }
    return batteries.get(storageSerialNo) || []
  }, [storageSerialNo, batteries])

  const prevStorageSerialNo = usePrevious(storageSerialNo)

  useEffect(() => {
    setError(undefined)

    if (storageSerialNo !== prevStorageSerialNo) {
      loadBatteries()
    }
  }, [storageSerialNo]) // eslint-disable-line

  const loadBatteries = async (abortSignal?: AbortSignal): Promise<void> => {
    if (isUndefined(storageSerialNo)) {
      return
    }

    setIsLoading(true)

    const batteryList = isFarmMasterSelected
      ? await fetchRscpBatteriesMaster(storageSerialNo, authFetch, abortSignal)
      : await fetchRscpBatteries(storageSerialNo, authFetch, abortSignal)

    if (batteryList.hasErrors && batteryList.error) {
      setError(batteryList.error)
    }

    const promises = (batteryList.value || []).map(item => fetchRscpBattery(item.storageSerialNo, item.batteryIndex, authFetch, abortSignal))
    const results = await Promise.all(promises)
    const batteries = results.filter(result => !result.hasErrors).map(result => result.value) as FarmBatteryModel[]
    const batteriesDecorated = (batteryList.value || []).map((item, index) => ({ ...batteries[index], storageSerialNo: item.storageSerialNo }))
    setBatteries(storageSerialNo, batteriesDecorated)

    setIsLoading(false)
  }

  const updateSettings = async (storageSerialNr: string, batteryId: string, values: SettingsFormValues): Promise<unknown> => {
    /** TODO: Checking for required parameters necessary? */
    if (isUndefined(storageSerialNr) || isUndefined(batteryId)) {
      throw new Error(`Invalid activeStorageId or tabIndex (id: ${storageSerialNr}, tabIndex: ${batteryId})`)
    }

    const dto = settingsFormToDto(values)

    return updateRscpBatterySettings(storageSerialNr, batteryId, dto, authFetch)
  }

  const getBatteryTraining = async (storageSerialNr: string, batteryId: string): Promise<BatteryTrainingModel | null> => {
    const batteryTraining = await fetchBatteryTrainingMode(storageSerialNr, batteryId, authFetch)
    return batteryTraining.value
  }

  const updateBatteryTraining = async (storageSerialNr: string, batteryId: string, trainingMode: boolean): Promise<void> => {
    return updateBatteryTrainingMode(storageSerialNr, batteryId, authFetch, trainingMode)
  }

  return { updateSettings, getBatteryTraining, updateBatteryTraining, batteries: valuesByStorage, error, isLoading }
}

export const useBatteryPowerManagementList = (storageId?: string): [BatteryPowerManagementModel[] | null, boolean, boolean] => {
  const authFetch = useAuthFetchWithResponse()

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: ['battery-managements', storageId],
    queryFn: () => getBatteryPowerManagementByStorage(storageId, authFetch),
    enabled: !!storageId,
  })

  return [data || null, isLoading, isError]
}
